import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Database, GitBranch } from "lucide-react"

export function MySQLOverviewPage() {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Database className="h-10 w-10 text-primary" />
          <h1 className="text-5xl font-bold">NestJS MySQL Template</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed">
          Production-ready NestJS template with MySQL, TypeORM, JWT authentication, 
          OAuth integration, migrations, and role-based access control. Perfect for rapid development 
          with relational data modeling.
        </p>
        <div className="flex gap-3 pt-2">
          <Badge variant="secondary" className="text-base py-1.5 px-3">NestJS 11</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">MySQL</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">TypeORM</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">JWT Auth</Badge>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLinkCard
          title="Quick Start"
          description="Get the template running in minutes with our step-by-step guide"
          href="/nestjs/mysql/quick-start"
        />
        <QuickLinkCard
          title="Features"
          description="Explore all the built-in features and capabilities"
          href="/nestjs/mysql/features"
        />
        <QuickLinkCard
          title="Project Structure"
          description="Understand the codebase organization and file structure"
          href="/nestjs/mysql/structure"
        />
        <QuickLinkCard
          title="Migrations"
          description="Learn how to manage database migrations with TypeORM"
          href="/nestjs/mysql/migrations"
        />
        <QuickLinkCard
          title="Entities"
          description="Understand TypeORM entities and relationships"
          href="/nestjs/mysql/entities"
        />
        <QuickLinkCard
          title="Configuration"
          description="Learn how to configure the application and environment"
          href="/nestjs/mysql/configuration"
        />
        <QuickLinkCard
          title="Available Scripts"
          description="npm commands for development, migrations, and production"
          href="/nestjs/mysql/scripts"
        />
      </div>

      {/* What's Included */}
      <div className="bg-muted/30 rounded-xl p-8 space-y-6">
        <h2 className="text-3xl font-bold">What's Included</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Authentication & Security</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>JWT access & refresh tokens with rotation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Google OAuth 2.0 (extensible to other providers)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Role-based access control (RBAC)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Password hashing with bcrypt</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Device tracking for tokens</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Database & ORM</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>TypeORM for robust data modeling</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Migration system for schema changes</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Relationship management (OneToMany, ManyToOne)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Transaction support</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Query builder and repository pattern</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Developer Experience</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>TypeScript strict mode</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Swagger & Scalar API documentation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Global response wrapper</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Request validation with class-validator</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Hot reload and fast refresh</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Production Ready</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Environment configuration</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Error handling and logging</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>CORS and security headers</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Automated setup scripts</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Docker support</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Getting Started CTA */}
      <div className="bg-card border rounded-xl p-10 text-center space-y-6">
        <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Follow our quick start guide to have the template running in just a few minutes, 
          or dive deep into the documentation to understand every aspect of the template.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button asChild size="lg" className="text-base">
            <Link to="/nestjs/mysql/quick-start">
              Get Started
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <a
              href="https://github.com/ALIRAZA47/template-nestjs-mysql"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitBranch className="mr-2 h-5 w-5" />
              View on GitHub
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function QuickLinkCard({ 
  title, 
  description, 
  href 
}: { 
  title: string
  description: string
  href: string
}) {
  return (
    <Link 
      to={href}
      className="group block p-6 bg-card border rounded-xl hover:border-primary/50 transition-all hover:shadow-md"
    >
      <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
    </Link>
  )
}

